/**
 * VoicePanel Component
 * Expanded panel UI for voice interaction
 */

import React from 'react';
import clsx from 'clsx';
import type { VoiceState } from '@ai360smartsearch/shared';
import { AudioWaveform } from './AudioWaveform';
import './VoicePanel.css';

interface VoicePanelProps {
  voiceState: VoiceState;
  transcript?: string;
  isOpen: boolean;
  onStart: () => void;
  onStop: () => void;
  onClose: () => void;
}

export const VoicePanel: React.FC<VoicePanelProps> = ({
  voiceState,
  transcript,
  isOpen,
  onStart,
  onStop,
  onClose,
}) => {
  const getStatusText = () => {
    switch (voiceState) {
      case 'listening':
        return 'Escuchando tu voz...';
      case 'processing':
        return 'Procesando audio...';
      case 'speaking':
        return 'Reproduciendo respuesta...';
      default:
        return 'Pulsa el micrófono para comenzar';
    }
  };

  const isActive = voiceState !== 'idle';

  if (!isOpen) return null;

  return (
    <>
      <div className="ai360-voice-panel-overlay" onClick={onClose} />
      <div className="ai360-voice-panel">
        <div className="ai360-voice-panel-header">
          <h3 className="ai360-voice-panel-title">Asistente de Voz</h3>
          <button
            type="button"
            onClick={onClose}
            className="ai360-voice-panel-close"
            aria-label="Cerrar"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="ai360-voice-panel-body">
          <div className="ai360-voice-panel-visualizer">
            <button
              type="button"
              onClick={isActive ? onStop : onStart}
              className={clsx(
                'ai360-voice-panel-button',
                `ai360-voice-panel-button--${voiceState}`
              )}
              disabled={voiceState === 'processing' || voiceState === 'speaking'}
            >
              {voiceState === 'listening' ? (
                <AudioWaveform isActive={true} barCount={7} />
              ) : (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path
                    d="M16 4a4 4 0 0 0-4 4v8a4 4 0 0 0 8 0V8a4 4 0 0 0-4-4z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M24 12v4a8 8 0 1 1-16 0v-4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path d="M16 24v4m-4 0h8" stroke="currentColor" strokeWidth="2" />
                </svg>
              )}
            </button>
          </div>

          <p className="ai360-voice-panel-status">{getStatusText()}</p>

          {transcript && (
            <div className="ai360-voice-panel-transcript">
              <p className="ai360-voice-panel-transcript-label">Transcripción:</p>
              <p className="ai360-voice-panel-transcript-text">{transcript}</p>
            </div>
          )}

          <div className="ai360-voice-panel-tips">
            <p className="ai360-voice-panel-tips-title">Consejos:</p>
            <ul className="ai360-voice-panel-tips-list">
              <li>Habla claramente y a un ritmo normal</li>
              <li>Intenta reducir el ruido de fondo</li>
              <li>El sistema detectará automáticamente cuando termines de hablar</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};
